package br.com.duoma.duomaproducao.ui.components

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.snapshots.SnapshotStateMap
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.layout.positionInParent
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import androidx.constraintlayout.compose.ConstraintLayout
import br.com.duoma.duomaproducao.model.WorkCenter
import br.com.duoma.duomaproducao.ui.misc.mediaQuery
import br.com.duoma.duomaproducao.viewmodel.ExecutionInfo
import br.com.duoma.duomaproducao.viewmodel.monthAverage
import br.com.duoma.duomaproducao.viewmodel.quantityToday

@Composable
fun ProductionFlowchart(
    executions: ExecutionInfo,
    modifier: Modifier = Modifier,
    onClick: (WorkCenter) -> Unit = {}
) {
    val nodeRects = remember<SnapshotStateMap<String, NodeRect>> { mutableStateMapOf() }

    fun Modifier.track(tag: String) = this.onGloballyPositioned { coords ->
        val pos = coords.positionInParent()
        val size: IntSize = coords.size
        nodeRects[tag] = NodeRect(pos.x, pos.y, size.width.toFloat(), size.height.toFloat())
    }

    val margin = mediaQuery(32.dp, 28.dp)
    val horizontalPadding = mediaQuery(0.dp, 12.dp)
    val bottomPadding = mediaQuery(24.dp, 36.dp)

    Box(
        modifier = modifier
            .padding(horizontal = horizontalPadding, vertical = mediaQuery(
                0.dp,
                4.dp
            )
            )
            .padding(bottom = bottomPadding)
    ) {
        ConstraintLayout(modifier = Modifier.fillMaxSize()) {
            val (corte, costura, marcenaria, laminacao,
                montagem, preparacao, espumacao, tapecaria) = createRefs()

            val col1 = createGuidelineFromStart(0.00f)
            val col2 = createGuidelineFromStart(0.5f)
            val col3 = createGuidelineFromStart(1f)

            FlowChartNode(
                title = "Corte",
                today = executions[WorkCenter.CORTE].quantityToday(),
                weekAvg = executions[WorkCenter.CORTE].monthAverage(),
                modifier = Modifier
                    .track("corte")
                    .constrainAs(corte) {
                        top.linkTo(parent.top)
                        start.linkTo(col1)
                    },
                onClick = { onClick(WorkCenter.CORTE) }
            )

            FlowChartNode(
                title = "Costura",
                today = executions[WorkCenter.COSTURA].quantityToday(),
                weekAvg = executions[WorkCenter.COSTURA].monthAverage(),
                modifier = Modifier
                    .track("costura")
                    .constrainAs(costura) {
                        top.linkTo(corte.top)
                        end.linkTo(col3)
                    },
                onClick = { onClick(WorkCenter.COSTURA) }
            )

            FlowChartNode(
                title = "Marcenaria",
                today = executions[WorkCenter.MARCENARIA].quantityToday(),
                weekAvg = executions[WorkCenter.MARCENARIA].monthAverage(),
                modifier = Modifier
                    .track("marcenaria")
                    .constrainAs(marcenaria) {
                        top.linkTo(corte.bottom, margin)
                        start.linkTo(col1)
                    },
                onClick = { onClick(WorkCenter.MARCENARIA) }
            )

            FlowChartNode(
                title = "Laminação",
                today = executions[WorkCenter.LAMINACAO].quantityToday(),
                weekAvg = executions[WorkCenter.LAMINACAO].monthAverage(),
                modifier = Modifier
                    .track("laminacao")
                    .constrainAs(laminacao) {
                        top.linkTo(marcenaria.bottom, margin)
                        start.linkTo(col1)
                    },
                onClick = { onClick(WorkCenter.LAMINACAO) }
            )

            FlowChartNode(
                title = "Montagem",
                today = executions[WorkCenter.MONTAGEM].quantityToday(),
                weekAvg = executions[WorkCenter.MONTAGEM].monthAverage(),
                modifier = Modifier
                    .track("montagem")
                    .constrainAs(montagem) {
                        top.linkTo(marcenaria.top)
                        centerAround(col2)
                    },
                onClick = { onClick(WorkCenter.MONTAGEM) }
            )

            FlowChartNode(
                title = "Preparação",
                today = executions[WorkCenter.PREPARACAO_EST].quantityToday(),
                weekAvg = executions[WorkCenter.PREPARACAO_EST].monthAverage(),
                modifier = Modifier
                    .track("preparacao")
                    .constrainAs(preparacao) {
                        top.linkTo(montagem.bottom, margin)
                        centerAround(col2)
                    },
                onClick = { onClick(WorkCenter.PREPARACAO_EST) }
            )

            FlowChartNode(
                title = "Espumação",
                today = executions[WorkCenter.ESPUMACAO].quantityToday(),
                weekAvg = executions[WorkCenter.ESPUMACAO].monthAverage(),
                modifier = Modifier
                    .track("espumacao")
                    .constrainAs(espumacao) {
                        top.linkTo(preparacao.top)
                        end.linkTo(col3)
                    },
                onClick = { onClick(WorkCenter.ESPUMACAO) }
            )

            FlowChartNode(
                title = "Tapeçaria",
                today = executions[WorkCenter.TAPECARIA].quantityToday(),
                weekAvg = executions[WorkCenter.TAPECARIA].monthAverage(),
                modifier = Modifier
                    .track("tapecaria")
                    .constrainAs(tapecaria) {
                        top.linkTo(costura.bottom)
                        bottom.linkTo(espumacao.top)
                        end.linkTo(col3)
                    },
                onClick = { onClick(WorkCenter.TAPECARIA) }
            )
        }

        FlowchartArrows(
            rects = nodeRects,
            color = MaterialTheme.colorScheme.primary,
            bottomArrowMargin = margin
        )
    }
}
